import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { finishLogin } from '../../../auth/oidc'
import { getOidcConfig } from '../../../auth/config'
import {
  clearIamNewUserHint,
  clearOidcOverride,
  clearOidcRetry,
  loadIamNewUserHint,
} from '../../../auth/state'
import { UnifiedLoadingShell } from '../../../components/auth/UnifiedLoadingShell'

function humanizeOidcError(detail) {
  const text = String(detail || '').trim()
  if (!text) return { title: 'Sign-in failed', message: 'Unknown error.' }

  const lower = text.toLowerCase()
  if (lower.includes('invalid_client') && lower.includes('unknown client_id')) {
    return {
      title: 'Sign-in failed',
      message: 'OIDC client_id unknown. Please rebuild with valid OIDC env.',
    }
  }

  if (text.includes('invalid_grant') && (lower.includes('expired code') || lower.includes('invalid or expired code'))) {
    return {
      title: 'Session expired',
      message: 'This sign-in link has expired. Please sign in again.',
    }
  }

  if (text.includes('token_exchange_timeout')) {
    return { title: 'IAM timeout', message: 'IAM token exchange timed out after 15 seconds. Please retry.' }
  }

  if (text.includes('token_exchange_failed')) {
    return { title: 'Sign-in failed', message: 'Token exchange failed. Please retry.' }
  }

  if (text.includes('missing_code_or_state')) {
    return { title: 'Invalid callback', message: 'Missing authorization code/state.' }
  }

  if (text.includes('missing_oidc_config')) {
    return { title: 'Not configured', message: 'OIDC config is missing on this build.' }
  }

  return { title: 'Sign-in failed', message: text }
}

export default function OidcCallback() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const cfg = useMemo(() => getOidcConfig(), [])
  const [status, setStatus] = useState('working')
  const [detail, setDetail] = useState('')
  const startedRef = useRef(false)

  useEffect(() => {
    if (startedRef.current) return
    startedRef.current = true
    ;(async () => {
      const error = params.get('error')
      const errorDescription = params.get('error_description')
      if (error) {
        setStatus('error')
        setDetail(`${error}${errorDescription ? `: ${errorDescription}` : ''}`)
        return
      }

      if (!cfg) {
        setStatus('error')
        setDetail('missing_oidc_config')
        return
      }

      const code = params.get('code') || ''
      const state = params.get('state') || ''
      if (!code || !state) {
        setStatus('error')
        setDetail('missing_code_or_state')
        return
      }

      try {
        const r = await finishLogin(code, state)
        setStatus('ok')
        clearOidcRetry()
        // Token exchange alone does not prove the protected API accepts this session.
        const hint = loadIamNewUserHint()
        if (hint?.sub) {
          try {
            const dest = r.next || '/monitor'
            const u = new URL(dest, window.location.origin)
            u.searchParams.set('iam_new_user', '1')
            u.searchParams.set('iam_sub', hint.sub)
            if (hint.app) u.searchParams.set('iam_app', hint.app)
            clearIamNewUserHint()
            navigate(u.pathname + u.search + u.hash, { replace: true })
            return
          } catch {
            clearIamNewUserHint()
          }
        }
        navigate(r.next || '/monitor', { replace: true })
      } catch (e) {
        setStatus('error')
        setDetail(String(e?.message || e || 'token_exchange_failed'))
      }
    })()
  }, [cfg, navigate, params])

  if (status === 'working' || status === 'ok') {
    return <UnifiedLoadingShell title="Loading…" subtitle="Please wait…" />
  }

  const err = humanizeOidcError(detail)

  return (
    <UnifiedLoadingShell title={err.title} subtitle={err.message}>
      <button
        type="button"
        className="w-full rounded-2xl bg-brand-pink text-white font-extrabold py-3"
        onClick={() => navigate('/login?fresh=1&auto=0', { replace: true })}
      >
        Back to sign in
      </button>
      <button
        type="button"
        className="w-full rounded-2xl bg-brand-surface border-2 border-brand-textDark/20 text-brand-textDark font-extrabold py-3"
        onClick={() => {
          clearOidcOverride()
          clearOidcRetry()
          navigate('/login?fresh=1&auto=0', { replace: true })
        }}
      >
        Reset config
      </button>
    </UnifiedLoadingShell>
  )
}
